import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDirty } from '../../lib/dirty';
import { FileText, List, MoreHorizontal, Plus, Send } from 'lucide-react';
import { enumLabel } from '../../lib/labels';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiGetFresh, apiPost, apiPut } from '../../lib/api-client';
import { lookupChoice } from '../../lib/select-choices';
import { blankVisibleKey, copyDocument, editableStatus, stashDuplicate } from '../../lib/duplicate';
import { AddOfferPartDialog } from '../documents/AddOfferPartDialog';
import { RecordAssistDialog } from './RecordAssistDialog';
import { assistEditable, assistKind, assistOpen } from '../../lib/record-assist';
import { OfferDuplicateDialog } from '../documents/OfferDuplicateDialog';
import { CreateInvoiceDialog } from './CreateInvoiceDialog';
import { ListQueryBar } from './ListQueryBar';
import { applyTableQuery, columnParam, deleteUrl, displayField, editDraft, emptyDraft, excelCell, flattenRow, nextSort, pageCount, readPageSize, recordListQuery, toPayload, writePageSize, type ColumnSort, type Row, type SalesOffer } from '../../lib/list-query';
import { poundsToPence, sterlingBooksLine } from '../../lib/money';
import type { FieldDef, RecordSchema } from '../../lib/record-schemas';
import { apiError, sharePointLinks } from '../../lib/utils';
import { useLedgerReload } from '../../lib/use-ledger';
import { recordLabel, recordWorkspaceLayout } from '../../lib/record-workspace';
import { useAuth } from '../../contexts/AuthContext';
import { Alert } from '../ui/Badge';
import { useNotice } from '../ui/Notice';
import { Button } from '../ui/Button';
import { SideBySideDiff } from '../ui/SideBySideDiff';
import { Dialog } from '../ui/Dialog';
import { DropdownMenu } from '../ui/DropdownMenu';
import { NewOfferDialog } from '../documents/NewOfferDialog';
import { Field, Input, Select, TextArea } from '../ui/Input';
import { ChipList } from '../ChipList';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../ui/ColumnSearch';
import { ProposalPage } from '../../pages/Proposal';
import { QuotationPage } from '../../pages/Quotation';
import { formatMoney } from '../../lib/money';
import { ActionCluster, ContextPanel, ExcelButton, FilterTabs, PageHead, PageSizeSelect, RecordLink, SortHeader, StatusPill } from '../ui/chrome';

const PANEL = new Set(['/api/reporting-customers', '/api/customers', '/api/engagements', '/api/charge-lines', '/api/vendors', '/api/bills', '/api/expenses', '/api/people', '/api/payments-in', '/api/payments-out']);
const SUBTITLE: Record<string, string> = {
  '/api/commercial': 'Proposals and quotations, together in one offer.',
  '/api/engagements': 'Manage customer work and commercial relationships.',
  '/api/reporting-customers': 'Parent customers used for consolidated reporting.',
  '/api/customers': 'Organisations you invoice.',
  '/api/bills': 'Supplier bills and the match against time.',
  '/api/invoices': 'Customer invoices.',
  '/api/invoice-templates': 'A template fills a new invoice, or creates the next one on a schedule.',
  '/api/purchase-orders': 'Customer orders and the lines they fund.',
  '/api/charge-lines': 'How work is priced.',
  '/api/vendors': 'Suppliers you pay.',
  '/api/expenses': 'Expenses waiting to be paid.',
  '/api/people': 'People who bill time.',
  '/api/payments-in': 'Money received.',
  '/api/payments-out': 'Money paid.',
};

export function RecordList({ schema }: { schema: RecordSchema }) {
  const notice = useNotice();
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [queryText, setQueryText] = useState('');
  const columnSearch = useColumnSearch();
  const cols = columnParam(columnSearch.applied);
  const [sort, setSort] = useState<ColumnSort | null>(() => {
    const params = new URLSearchParams(location.search);
    const key = params.get('sort');
    if (!key) return null;
    return { key, dir: params.get('dir') === 'desc' ? 'desc' : 'asc' };
  });
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceSort, setInvoiceSort] = useState<ColumnSort | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => readPageSize(schema.url));
  const [filterId, setFilterId] = useState(() => new URLSearchParams(location.search).get('filter') || schema.defaultFilter || schema.filters?.[0]?.id || 'all');
  const [appliedSql, setAppliedSql] = useState('');
  const engagementId = new URLSearchParams(location.search).get('engagementId') || '';
  const engagementName = new URLSearchParams(location.search).get('engagementName') || '';
  const poId = new URLSearchParams(location.search).get('poId') || '';
  const poNumber = new URLSearchParams(location.search).get('poNumber') || '';
  const invoiceId = new URLSearchParams(location.search).get('invoiceId') || '';
  const invoiceNumber = new URLSearchParams(location.search).get('invoiceNumber') || '';
  const billId = new URLSearchParams(location.search).get('billId') || '';
  const listFrom = new URLSearchParams(location.search).get('from') || '';
  const listTo = new URLSearchParams(location.search).get('to') || '';
  const listKind = new URLSearchParams(location.search).get('kind') || '';
  const billNumber = new URLSearchParams(location.search).get('billNumber') || '';
  const [form, setForm] = useState<Row | null>(null);
  const [askInvoice, setAskInvoice] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [invoiceRow, setInvoiceRow] = useState<Row | null>(null);
  const [offerDelete, setOfferDelete] = useState<SalesOffer | null>(null);
  const [addPart, setAddPart] = useState<{ kind: 'proposal' | 'quotation'; offer: SalesOffer } | null>(null);
  const [offerCopy, setOfferCopy] = useState<SalesOffer | null>(null);
  const [askNewOffer, setAskNewOffer] = useState(false);
  const [assist, setAssist] = useState<{ mode: 'create' | 'edit'; recordId?: string; current?: Row } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Row | null>(null);
  const [paneTab, setPaneTab] = useState('edit');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{ type: string; id: string } | null>(null);
  const [history, setHistory] = useState<{ id: string; at: string; actor: string; changes: { field: string; from: unknown; to: unknown }[] }[]>([]);
  const [offerTab, setOfferTab] = useState('all');
  const [customerFilter, setCustomerFilter] = useState(() => new URLSearchParams(location.search).get('customerId') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [hiddenCols, setHiddenCols] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(`bo-cols:${schema.url}`) || '[]') as string[]; } catch { return []; }
  });
  const baseline = useRef('');
  const dirty = useDirty();
  const [lookups, setLookups] = useState<Record<string, { value: string; label: string; code?: string; name?: string; description?: string; customerName?: string; engagementName?: string }[]>>({});

  function pageRequest(pageValue: string, pageSizeValue: string) {
    return recordListQuery({
      url: schema.url,
      page: pageValue,
      pageSize: pageSizeValue,
      q: queryText,
      sort,
      filter: schema.url === '/api/commercial' ? offerTab : filterId,
      customerId: customerFilter,
      from: listFrom,
      to: listTo,
      kind: listKind,
      status: statusFilter,
      engagementId,
      poId,
      invoiceId,
      billId,
      sql: appliedSql,
      cols,
    });
  }

  useEffect(() => {
    const timer = window.setTimeout(() => setQueryText(search), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    columnSearch.clear();
  }, [schema.url, columnSearch.clear]);

  const colsPage = useRef(cols);
  useEffect(() => {
    if (colsPage.current === cols) return;
    colsPage.current = cols;
    setPage(1);
  }, [cols]);

  const query = useQuery({
    queryKey: ['records', schema.url, page, pageSize, queryText, sort?.key || '', sort?.dir || '', filterId, offerTab, customerFilter, listFrom, listTo, listKind, statusFilter, engagementId, poId, invoiceId, billId, appliedSql, cols],
    queryFn: async () => {
      const data = await apiGet<{ items: Row[]; total: number; counts: Record<string, number>; columns?: string[] }>(pageRequest(String(page), String(pageSize)));
      return {
        items: (data.items || []).map(flattenRow),
        total: data.total || 0,
        counts: data.counts || {},
        columns: data.columns,
      };
    },
  });

  const schemaUrl = useRef(schema.url);
  const recordId = new URLSearchParams(location.search).get('record') || '';
  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get('filter');
    if (schemaUrl.current !== schema.url) {
      schemaUrl.current = schema.url;
      setSearch('');
      setQueryText('');
      setSort(null);
      setPage(1);
      setPageSize(readPageSize(schema.url));
      setFilterId(fromUrl || schema.defaultFilter || schema.filters?.[0]?.id || 'all');
      setAppliedSql('');
      setForm(null);
    } else if (fromUrl) {
      setFilterId(fromUrl);
    }
    let cancelled = false;
    void Promise.all((schema.lookups || []).map(async (lookup) => {
      const list = await apiGet<Row[]>(lookup.url);
      return {
        key: lookup.key,
        options: (list || []).map((item) => lookupChoice(lookup.url, item, lookup.label)),
      };
    })).then((sets) => {
      if (cancelled) return;
      const next: Record<string, { value: string; label: string }[]> = {};
      for (const set of sets) next[set.key] = set.options;
      setLookups(next);
    }).catch(() => {
      if (!cancelled) setLookups({});
    });
    return () => {
      cancelled = true;
    };
  }, [schema, location.search]);

  useEffect(() => {
    if (schema.detailPath && location.pathname.startsWith(`${schema.detailPath}/`) && !PANEL.has(schema.url)) setForm(null);
  }, [location.pathname, schema.detailPath, schema.url]);

  function reload() {
    void queryClient.invalidateQueries({ queryKey: ['records', schema.url] });
  }
  useLedgerReload(schema.url.replace(/^\/api\//, ''), reload);

  const tableFields = useMemo(() => schema.fields.filter((field) => !field.hideInTable), [schema]);
  const queryFields = (query.data?.columns || []).map((key) => tableFields.find((field) => field.key === key) || schema.fields.find((field) => field.key === key) || { key, label: key, type: key.endsWith('Pence') ? 'money' as const : 'text' as const });
  const rows = query.data?.items || [];
  const total = query.data?.total || 0;
  const pages = pageCount(total, pageSize);
  const shownFields = queryFields.length ? queryFields : tableFields.filter((field) => !hiddenCols.includes(field.key));
  const offerLayout = schema.url === '/api/commercial' && !queryFields.length;
  const fromRow = total ? (Math.min(page, pages) - 1) * pageSize + 1 : 0;
  const toRow = Math.min(total, Math.min(page, pages) * pageSize);
  const idKey = schema.idKey || 'id';
  const canWrite = can(schema.page, schema.writeAction);
  function offerOf(row: Row): SalesOffer {
    return row as unknown as SalesOffer;
  }
  const canVoid = can(schema.page, 'void');
  const workspace = recordWorkspaceLayout(PANEL.has(schema.url), Boolean(form), isNew);
  const singularLabel = recordLabel(schema.title);

  function selectOptions(field: FieldDef) {
    if (field.lookup) return lookups[field.lookup] || [];
    return (field.options || []).map((value) => ({ value, label: enumLabel(value) }));
  }

  async function reloadLookup(key: string) {
    const lookup = (schema.lookups || []).find((item) => item.key === key);
    if (!lookup) return;
    const list = await apiGetFresh<Row[]>(lookup.url);
    setLookups((current) => ({ ...current, [key]: (list || []).map((item) => lookupChoice(lookup.url, item, lookup.label)) }));
  }

  function fieldSnapshot(row: Row): string {
    const picked: Record<string, unknown> = {};
    for (const field of schema.fields) {
      if (field.hideInForm) continue;
      const value = row[field.key];
      picked[field.key] = value == null ? '' : value;
    }
    return JSON.stringify(picked);
  }

  function adopt(draft: Row, nextNew: boolean) {
    baseline.current = fieldSnapshot(draft);
    setIsNew(nextNew);
    setForm(draft);
    dirty.setDirty(false);
  }

  const openedRecord = useRef('');
  useEffect(() => {
    if (!recordId || !PANEL.has(schema.url)) return;
    if (openedRecord.current === recordId) return;
    const row = (query.data?.items || []).find((item) => String(item[idKey] || '') === recordId);
    if (!row) return;
    openedRecord.current = recordId;
    adopt(editDraft(flattenRow(row), schema.fields), false);
  }, [recordId, query.data, schema, idKey]);

  async function save() {
    if (!form) return;
    await notice.run('Saving', async () => {
      const body = toPayload(form, schema.fields);
      const writeUrl = schema.url.split('?')[0];
      if (schema.url.includes('direction=supplier')) body.direction = 'supplier';
      const saved = (isNew ? await apiPost<Row>(writeUrl, body) : await apiPut<Row>(`${writeUrl}/${form[idKey]}`, body));
      const next = flattenRow({ ...form, ...saved });
      baseline.current = fieldSnapshot(editDraft(next, schema.fields));
      setIsNew(false);
      setForm(editDraft(next, schema.fields));
      dirty.setDirty(false);
      reload();
    }, 'Saved', { confirm: false });
  }

  async function remove(row: Row) {
    await notice.run('Deleting', async () => {
      await apiDelete(deleteUrl(schema.url, row, row[idKey]));
      setPendingDelete(null);
      if (form && form[idKey] === row[idKey]) setForm(null);
      reload();
    }, 'Deleted', { confirm: false });
  }

  async function removeOffer(offer: SalesOffer, which: 'proposal' | 'quotation' | 'both') {
    await notice.run('Deleting', async () => {
      if ((which === 'proposal' || which === 'both') && offer.proposalId) await apiDelete(`/api/proposals/${offer.proposalId}`);
      if ((which === 'quotation' || which === 'both') && offer.quotationId) await apiDelete(`/api/quotations/${offer.quotationId}`);
      setOfferDelete(null);
      reload();
    }, 'Deleted', { confirm: false });
  }

  function listQuery(): string {
    const params = new URLSearchParams();
    const active = schema.url === '/api/commercial' ? offerTab : filterId;
    if (active && active !== 'all') params.set('filter', active);
    if (sort) {
      params.set('sort', sort.key === 'offer' ? 'title' : sort.key);
      params.set('dir', sort.dir);
    }
    if (queryText) params.set('q', queryText);
    if (customerFilter) params.set('customerId', customerFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (engagementId) params.set('engagementId', engagementId);
    if (poId) params.set('poId', poId);
    if (invoiceId) params.set('invoiceId', invoiceId);
    if (billId) params.set('billId', billId);
    if (appliedSql) params.set('sql', appliedSql);
    const text = params.toString();
    return text ? `?${text}` : '';
  }

  function openRow(row: Row, tab?: string) {
    const go = () => {
      setPaneTab(tab || 'edit');
      setHistoryOpen(false);
      const search = listQuery();
      if (schema.url === '/api/commercial') {
        const offer = offerOf(row);
        if (tab === 'quotation' && offer.quotationId) navigate(`/quotations/${offer.quotationId}${search}`);
        else if (tab === 'proposal' && offer.proposalId) navigate(`/proposals/${offer.proposalId}${search}`);
        else if (offer.proposalId) navigate(`/proposals/${offer.proposalId}${search}`);
        else if (offer.quotationId) navigate(`/quotations/${offer.quotationId}${search}`);
        return;
      }
      if (PANEL.has(schema.url)) {
        adopt(editDraft(row, schema.fields), false);
        return;
      }
      if (schema.detailPath) {
        setForm(null);
        baseline.current = '';
        dirty.setDirty(false);
        navigate(`${schema.detailPath}/${row[idKey]}${search}`);
      }
    };
    dirty.requestLeave(go);
  }

  const routeId = schema.detailPath && location.pathname.startsWith(`${schema.detailPath}/`)
    ? decodeURIComponent(location.pathname.slice(schema.detailPath.length + 1))
    : '';

  useEffect(() => {
    const nested = schema.url === '/api/commercial' || Boolean(schema.detailPath && routeId);
    if (nested) return;
    const changed = form != null && fieldSnapshot(form) !== baseline.current;
    dirty.setDirty(changed);
    dirty.setSaver(changed ? save : null);
    return () => {
      dirty.setDirty(false);
      dirty.setSaver(null);
    };
  }, [form, routeId, schema.url, schema.detailPath]);

  useEffect(() => {
    const onLeave = (event: BeforeUnloadEvent) => {
      if (!dirty.dirty) return;
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [dirty.dirty]);

  function setField(key: string, value: unknown) {
    setForm((current) => {
      if (!current) return current;
      const next = { ...current, [key]: value };
      if (key === 'engagementId' && schema.url === '/api/purchase-orders') {
        const match = (lookups.engagementId || []).find((item) => item.value === value);
        next.customerName = match?.customerName || '';
        next.engagementName = match?.engagementName || '';
        delete next.customerId;
      }
      return next;
    });
  }

  const duplicateLists = new Set(['/api/customers', '/api/engagements', '/api/charge-lines', '/api/purchase-orders', '/api/purchase-orders?direction=supplier', '/api/invoices', '/api/commercial', '/api/vendors']);

  function sourceRow(row: Row): Row {
    return flattenRow((query.data?.items || []).find((item) => item[idKey] === row[idKey]) || row);
  }

  async function duplicate(row: Row) {
    await notice.run('Duplicating', async () => {
      if (schema.url === '/api/commercial') {
        setOfferCopy(offerOf(row));
        return;
      }
      if (schema.url === '/api/invoices') {
        const full = await apiGet<Row>(`/api/invoices/${row[idKey]}`);
        stashDuplicate('invoice', copyDocument(full, 'draft'));
        navigate('/invoices/new?duplicate=1');
        return;
      }
      if (schema.url.startsWith('/api/purchase-orders')) {
        const copy = blankVisibleKey(schema.url, copyDocument(sourceRow(row), editableStatus(row.status, 'open')));
        copy.direction = schema.url.includes('direction=supplier') ? 'supplier' : (copy.direction || 'customer');
        stashDuplicate('purchase-order', copy);
        navigate(`${schema.newPath}?duplicate=1`);
        return;
      }
      const edited = editDraft(sourceRow(row), schema.fields);
      const draft = emptyDraft(schema.fields);
      for (const field of schema.fields) {
        if (field.hideInForm) continue;
        draft[field.key] = edited[field.key];
      }
      adopt(blankVisibleKey(schema.url, draft), true);
    }, 'Duplicated');
  }

  function startNew() {
    if (assistKind(schema.url)) {
      setAssist({ mode: 'create' });
      return;
    }
    if (schema.newPath) {
      navigate(schema.newPath);
      return;
    }
    adopt(emptyDraft(schema.fields), true);
  }

  function manualNew() {
    setAssist(null);
    if (schema.url === '/api/commercial') {
      setAskNewOffer(true);
      return;
    }
    if (schema.newPath) {
      navigate(schema.newPath);
      return;
    }
    adopt(emptyDraft(schema.fields), true);
  }

  function showActivity() {
    setPaneTab('activity');
    const type = historyTypeFor(schema.url);
    const recordId = form ? String(form[idKey] || '') : '';
    if (!type || !recordId) {
      setHistory([]);
      return;
    }
    void apiGet<typeof history>(`/api/revisions/${type}/${recordId}`).then((rows) => {
      setHistory(rows);
      setHistoryTarget({ type, id: recordId });
    }).catch((err) => notice.failure(apiError(err, 'Could not load history')));
  }

  if (schema.url === '/api/commercial' && location.pathname !== '/quotations') return <Outlet />;
  if (schema.detailPath && routeId && schema.url !== '/api/bills') return <Outlet />;
  if (schema.url === '/api/bills' && routeId) return <Outlet />;

  function dropQuery(keys: string[]) {
    const params = new URLSearchParams(location.search);
    for (const key of keys) params.delete(key);
    const next = params.toString();
    setPage(1);
    navigate(next ? `${location.pathname}?${next}` : location.pathname);
  }

  const section = schema.url.includes('supplier') || schema.url.includes('vendor') || schema.url.includes('bill') || schema.url.includes('expense') || schema.url.includes('people') || schema.url.includes('payments-out') ? 'Payable' : 'Receivable';
  const offerCounts = schema.url === '/api/commercial' ? {
    all: query.data?.counts.all || 0,
    drafts: query.data?.counts.drafts || 0,
    issued: query.data?.counts.issued || 0,
    accepted: query.data?.counts.accepted || 0,
  } : null;

  return (
    <div>
      <PageHead
        crumbs={[{ label: section }, { label: schema.title }, ...(workspace.fullCreate ? [{ label: 'New' }] : [])]}
        title={workspace.fullCreate ? `New ${singularLabel}` : schema.title}
        subtitle={workspace.fullCreate ? 'Complete the details, then save the new record.' : SUBTITLE[schema.url] || `${total} records`}
        actionsInline
        actions={canWrite && !workspace.fullCreate ? (
          <Button onClick={() => startNew()}><Plus className="mr-1 h-4 w-4" />{schema.url === '/api/commercial' ? 'New offer' : `New ${singularLabel}`}</Button>
        ) : null}
      />
      {workspace.showList ? <div className={workspace.split ? 'max-md:hidden' : ''}>
      {appliedSql ? null : offerCounts ? (
        <FilterTabs active={offerTab} onChange={(id) => { setOfferTab(id); setPage(1); }} tabs={[
          { id: 'all', label: 'All offers', count: offerCounts.all },
          { id: 'drafts', label: 'Drafts', count: offerCounts.drafts },
          { id: 'issued', label: 'Issued', count: offerCounts.issued },
          { id: 'accepted', label: 'Accepted', count: offerCounts.accepted },
        ]} />
      ) : schema.filters?.length ? (
        <FilterTabs active={filterId} onChange={(id) => { setFilterId(id); setPage(1); }} tabs={schema.filters.map((item) => ({ id: item.id, label: item.label, count: query.data?.counts[item.id] }))} />
      ) : null}
      <div className="bo-filter-row mb-4 flex flex-wrap items-center gap-3">
        <Input className="max-w-sm" placeholder={schema.url === '/api/commercial' ? 'Search offers...' : `Search ${schema.title.toLowerCase()}...`} value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
        {lookups.customerId ? (
          <Select searchable className="max-w-[220px]" choices={[{ value: '', label: 'All customers' }, ...lookups.customerId]} onOpen={() => reloadLookup('customerId')} value={customerFilter} onChange={(event) => { setCustomerFilter(event.target.value); setPage(1); }} />
        ) : null}
        {schema.fields.some((field) => field.key === 'status') ? (
          <Select className="max-w-[180px]" value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}>
            <option value="">All statuses</option>
            {(schema.fields.find((field) => field.key === 'status')?.options || []).map((item) => <option key={item} value={item}>{enumLabel(item)}</option>)}
          </Select>
        ) : null}
        {engagementId ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-[var(--bo-border)] px-3 py-1 text-sm">
            Engagement {engagementName || 'filter'}
            <button type="button" className="text-[var(--bo-link)]" onClick={() => { setPage(1); navigate(location.pathname); }}>Clear</button>
          </span>
        ) : null}
        {poId ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-[var(--bo-border)] px-3 py-1 text-sm">
            Purchase order {poNumber || 'filter'}
            <button type="button" className="text-[var(--bo-link)]" onClick={() => dropQuery(['poId', 'poNumber'])}>Clear</button>
          </span>
        ) : null}
        {invoiceId ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-[var(--bo-border)] px-3 py-1 text-sm">
            Invoice {invoiceNumber || 'filter'}
            <button type="button" className="text-[var(--bo-link)]" onClick={() => dropQuery(['invoiceId', 'invoiceNumber'])}>Clear</button>
          </span>
        ) : null}
        {billId ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-[var(--bo-border)] px-3 py-1 text-sm">
            Bill {billNumber || 'filter'}
            <button type="button" className="text-[var(--bo-link)]" onClick={() => dropQuery(['billId', 'billNumber'])}>Clear</button>
          </span>
        ) : null}
        <DropdownMenu
          trigger={<Button variant="outline"><MoreHorizontal className="mr-1 h-4 w-4" />Columns</Button>}
          items={tableFields.map((field) => ({
            label: `${hiddenCols.includes(field.key) ? 'Show' : 'Hide'} ${field.label}`,
            onClick: () => setHiddenCols((current) => {
              const next = current.includes(field.key) ? current.filter((key) => key !== field.key) : [...current, field.key];
              localStorage.setItem(`bo-cols:${schema.url}`, JSON.stringify(next));
              return next;
            }),
          }))}
        />
      </div>
      <ListQueryBar resource={schema.url} applied={appliedSql} onApply={(sql) => { setAppliedSql(sql.trim()); setPage(1); }} onClear={() => { setAppliedSql(''); setPage(1); }} />
      </div> : null}
      {workspace.showList && query.error ? <Alert error>{apiError(query.error, 'Could not load')}</Alert> : null}
      <div className={workspace.split ? 'grid items-stretch gap-4 max-md:block md:h-[calc(100vh-13rem)] xl:grid-cols-[3fr_2fr]' : ''}>
      {workspace.showList ? <div className={`flex min-h-0 min-w-0 flex-1 flex-col ${workspace.split ? 'max-md:hidden' : ''}`}>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)]">
          <div className="flex shrink-0 items-center justify-between border-b border-[var(--bo-border)] px-4 py-3 text-sm text-[var(--bo-muted)]">
            <span className="flex items-center gap-3">
              <span>Showing {fromRow}–{toRow} of {total}</span>
              <PageSizeSelect value={pageSize} onChange={(size) => { setPageSize(size); writePageSize(schema.url, size); setPage(1); }} />
              <ExcelButton filename={schema.title} disabled={total === 0} load={async () => {
                const data = await apiGet<{ items: Row[] }>(pageRequest('1', 'all'));
                const items = (data.items || []).map(flattenRow);
                if (offerLayout) {
                  return {
                    headers: ['Offer', 'Number', 'Customer', 'Documents', 'Status', 'Issued'],
                    rows: items.map((row) => {
                      const offer = offerOf(row);
                      const documents = [offer.proposalId ? 'Proposal' : '', offer.quotationId ? 'Quotation' : ''].filter(Boolean).join(', ');
                      return [offer.title, offer.number, offer.customerName, documents, offer.status, formatUpdated(offer.updated)];
                    }),
                  };
                }
                return {
                  headers: shownFields.map((field) => field.label),
                  rows: items.map((row) => shownFields.map((field) => excelCell(row, field, lookups))),
                };
              }} />
            </span>
            <span className="flex items-center gap-1">
              <button type="button" className="rounded-lg border border-[var(--bo-border)] px-2 py-1 disabled:opacity-40" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>‹</button>
              {Array.from({ length: Math.min(5, pages) }, (_, index) => Math.max(1, Math.min(Math.min(page, pages) - 2, pages - 4)) + index).filter((n) => n <= pages).map((n) => (
                <button key={n} type="button" className={`h-8 w-8 rounded-lg ${n === Math.min(page, pages) ? 'bg-[#895CF6] text-white' : ''}`} onClick={() => setPage(n)}>{n}</button>
              ))}
              <button type="button" className="rounded-lg border border-[var(--bo-border)] px-2 py-1 disabled:opacity-40" disabled={page >= pages} onClick={() => setPage((n) => n + 1)}>›</button>
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
          <table className="bo-cards min-w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-[var(--bo-faint)]">
              <tr>
                {offerLayout ? (
                  <>
                    <SortHeader label="Offer" active={sort?.key === 'offer'} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, 'offer')); setPage(1); }} />
                    <SortHeader label="Customer" active={sort?.key === 'customerName'} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, 'customerName')); setPage(1); }} />
                    <SortHeader label="Documents" active={sort?.key === 'documents'} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, 'documents')); setPage(1); }} />
                    <SortHeader label="Status" active={sort?.key === 'status'} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, 'status')); setPage(1); }} />
                  </>
                ) : shownFields.map((col) => (
                  <SortHeader key={col.key} label={col.label} active={sort?.key === col.key} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, col.key)); setPage(1); }} />
                ))}
                {offerLayout ? <SortHeader label="Issued" active={sort?.key === 'updated'} dir={sort?.dir || 'asc'} onSort={() => { setSort((current) => nextSort(current, 'updated')); setPage(1); }} /> : null}
                <th className="px-4 py-3 text-right font-medium"><ColumnFilterToggle open={columnSearch.open} onClick={columnSearch.toggle} /></th>
              </tr>
              {columnSearch.open ? (
                <ColumnFilterInputs
                  columns={offerLayout
                    ? [
                      { key: 'title', label: 'Offer' },
                      { key: 'customerName', label: 'Customer' },
                      { key: 'documents', label: 'Documents' },
                      { key: 'status', label: 'Status' },
                      { key: 'updated', label: 'Issued' },
                    ]
                    : shownFields.map((field) => ({ key: field.key, label: field.label }))}
                  draft={columnSearch.draft}
                  onChange={columnSearch.set}
                  trailing
                />
              ) : null}
            </thead>
            <tbody>
              {rows.map((row) => {
                const selected = form && !isNew && String(form[idKey] || '') === String(row[idKey]);
                const unallocated = row.unallocated === true;
                return (
                <tr key={String(row[idKey])} className={`cursor-pointer border-t border-[var(--bo-border)] ${selected ? 'bg-[#895CF6]/20' : unallocated ? 'bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-950/60' : 'hover:bg-[var(--bo-hover)]'}`} onClick={() => openRow(row)}>
                  {offerLayout ? (
                    <>
                      <td className="px-4 py-4" data-label="Offer">
                        <div className="font-medium">{offerOf(row).title}</div>
                        <div className="text-xs text-[var(--bo-faint)]">{offerOf(row).number}</div>
                      </td>
                      <td className="px-4 py-4" data-label="Customer">{offerOf(row).customerName}</td>
                      <td className="px-4 py-4" data-label="Documents">
                        <span className="inline-flex gap-2">
                          {offerOf(row).proposalId ? <button type="button" className="inline-flex items-center gap-1 rounded-md border border-[var(--bo-border)] px-2 py-1 text-xs" onClick={(event) => { event.stopPropagation(); openRow(row, 'proposal'); }}><FileText className="h-3 w-3" />Proposal</button> : canWrite ? <button type="button" className="inline-flex items-center gap-1 rounded-md border border-[var(--bo-border)] px-2 py-1 text-xs" onClick={(event) => { event.stopPropagation(); setAddPart({ kind: 'proposal', offer: offerOf(row) }); }}>Add proposal</button> : null}
                          {offerOf(row).quotationId ? <button type="button" className="inline-flex items-center gap-1 rounded-md border border-[var(--bo-border)] px-2 py-1 text-xs" onClick={(event) => { event.stopPropagation(); openRow(row, 'quotation'); }}><FileText className="h-3 w-3" />Quotation</button> : canWrite ? <button type="button" className="inline-flex items-center gap-1 rounded-md border border-[var(--bo-border)] px-2 py-1 text-xs" onClick={(event) => { event.stopPropagation(); setAddPart({ kind: 'quotation', offer: offerOf(row) }); }}>Add quotation</button> : null}
                        </span>
                      </td>
                      <td className="px-4 py-4" data-label="Status"><StatusPill palette="offer" value={offerOf(row).status.split(' / ')[0] || 'Draft'} /></td>
                      <td className="px-4 py-4 text-sm text-[var(--bo-muted)]" data-label="Issued">{formatUpdated(offerOf(row).updated)}</td>
                    </>
                  ) : null}
                  {offerLayout ? null : shownFields.map((col, index) => (
                    <td key={col.key} className="px-4 py-4" data-label={col.label}>
                      {col.key === 'invoicedNetPence' ? (
                        <button type="button" className="underline" onClick={(event) => { event.stopPropagation(); setInvoiceSearch(''); setInvoiceSort(null); setInvoiceRow(row); }}>{displayField(row, col, lookups)}</button>
                      ) : col.key === 'sourceFileName' && sharePointLinks(row).length ? (
                        <span className="inline-flex flex-wrap gap-x-3" onClick={(event) => event.stopPropagation()}>
                          {sharePointLinks(row).map((file) => <a key={file.url} className="text-[var(--bo-link)] underline" href={file.url} target="_blank" rel="noreferrer">{file.name}</a>)}
                        </span>
                      ) : schema.url === '/api/payments-in' && col.key === 'invoiceNumber' && row.invoiceId ? (
                        <span onClick={(event) => event.stopPropagation()}>
                          <RecordLink to={`/invoices/${row.invoiceId}`} backLabel="Back to payments in">{String(row.invoiceNumber || 'Open invoice')}</RecordLink>
                        </span>
                      ) : schema.url === '/api/payments-out' && col.key === 'billNumber' && row.billId ? (
                        <span onClick={(event) => event.stopPropagation()}>
                          <RecordLink to={`/bills/${row.billId}`} backLabel="Back to payments out">{String(row.billNumber || 'Open bill')}</RecordLink>
                        </span>
                      ) : (schema.url === '/api/charge-lines' || schema.url === '/api/bills' || schema.url === '/api/payments-out') && col.key === 'poNumber' && row.poId ? (
                        <span onClick={(event) => event.stopPropagation()}>
                          <RecordLink to={`${schema.url === '/api/charge-lines' ? '/purchase-orders' : '/supplier-purchase-orders'}/${row.poId}`} backLabel={`Back to ${String(row.name || row.number || row.billNumber || 'record')}`}>{String(row.poNumber || 'Open order')}</RecordLink>
                        </span>
                      ) : col.key === 'status' ? (
                        <span className="inline-flex flex-wrap items-center gap-2">
                          <StatusPill value={String(row.status || '')} />
                          {row.unallocated ? <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">Unallocated</span> : null}
                          {row.booksKind === 'purchase_refund' ? <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-800 dark:bg-slate-800 dark:text-slate-100">Purchase Refund</span> : null}
                          {(row.creditTag === 'credited' || row.partCredited) && row.status !== 'credited' ? <StatusPill value={row.creditTag === 'credited' ? 'credited' : 'part_credited'} /> : null}
                        </span>
                      ) : index === 0 ? (
                        <button type="button" className="text-left font-medium text-[var(--bo-text)]" onClick={(event) => { event.stopPropagation(); openRow(row); }}>{displayField(row, col, lookups)}</button>
                      ) : displayField(row, col, lookups)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right" data-label="" onClick={(event) => event.stopPropagation()}>
                    <DropdownMenu
                      trigger={<button type="button" aria-label="Row actions" className="rounded-lg p-2 text-[var(--bo-muted)] hover:bg-[var(--bo-hover)]"><MoreHorizontal className="h-4 w-4" /></button>}
                      items={[
                        { label: 'Open', onClick: () => openRow(row) },
                        ...(canWrite && assistKind(schema.url) && assistKind(schema.url) !== 'sales-offer' ? [{
                          label: 'Edit with AI',
                          disabled: !assistEditable(assistKind(schema.url) || '', row.status),
                          title: assistEditable(assistKind(schema.url) || '', row.status) ? 'Edit with AI' : 'This status cannot be edited',
                          onClick: () => setAssist({ mode: 'edit', recordId: String(row[idKey] || ''), current: row }),
                        }] : []),
                        ...(canWrite && duplicateLists.has(schema.url) ? [{ label: 'Duplicate', onClick: () => void duplicate(row) }] : []),
                        ...(schema.url === '/api/invoices' ? [{ label: 'PDF', onClick: () => window.open(`/api/invoices/${row[idKey]}/pdf`, '_blank') }] : []),
                        ...((canWrite || canVoid) ? [{ label: 'Delete', className: 'text-red-400', onClick: () => { schema.url === '/api/commercial' ? setOfferDelete(offerOf(row)) : setPendingDelete(row); } }] : []),
                      ]}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length ? <p className="px-4 py-8 text-sm text-[var(--bo-muted)]">{query.isLoading ? 'Loading' : 'No records yet.'}</p> : null}
          </div>
        </div>
        {schema.url === '/api/commercial' ? (
          <div className="mt-4 flex gap-3 rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] px-4 py-3 text-sm">
            <div className="font-medium">One offer. Two documents.</div>
            <div className="text-[var(--bo-muted)]">Open an offer to manage its proposal, quotation and issue history.</div>
          </div>
        ) : null}
      </div> : null}
      {PANEL.has(schema.url) && form ? (
        <ContextPanel
          label={singularLabel}
          title={isNew ? `New ${singularLabel}` : String(form.name || form.number || form.title || 'Record')}
          subtitle={String(form.customerName || form.vendorName || '')}
          status={String(form.status || '')}
          onClose={() => { setForm(null); setIsNew(false); }}
        >
          {isNew ? <p className="mb-3 text-sm text-[var(--bo-muted)]">Not saved yet. The identifier is blank so this does not reuse the original. Save creates a new record.</p> : null}
          <div className="mb-4 flex gap-4 border-b border-[var(--bo-border)] text-sm">
            <button type="button" className={`border-b-2 pb-2 ${paneTab !== 'activity' ? 'border-[#895CF6]' : 'border-transparent text-[var(--bo-muted)]'}`} onClick={() => setPaneTab('edit')}>Overview</button>
            <button type="button" className={`border-b-2 pb-2 ${paneTab === 'activity' ? 'border-[#895CF6]' : 'border-transparent text-[var(--bo-muted)]'}`} onClick={() => showActivity()}>Activity</button>
          </div>
          {paneTab === 'activity' ? (
            <div className="space-y-3">
              {history.length ? history.map((entry) => (
                <div key={entry.id} className="rounded-lg border border-[var(--bo-border)] p-3 text-sm">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span>{new Date(entry.at).toLocaleString()} · {entry.actor}</span>
                    <Button variant="outline" onClick={() => {
                      if (!historyTarget) return;
                      void apiPost(`/api/revisions/${historyTarget.type}/${historyTarget.id}/restore`, { revisionId: entry.id }).then(() => { reload(); showActivity(); });
                    }}>Restore</Button>
                  </div>
                  {(entry.changes || []).map((change) => (
                    <div key={change.field} className="mt-2 text-sm">
                      <div className="font-medium">{change.field}</div>
                      <SideBySideDiff before={historyValue(change.field, change.from)} after={historyValue(change.field, change.to)} />
                    </div>
                  ))}
                </div>
              )) : <p className="text-sm text-[var(--bo-muted)]">No earlier versions.</p>}
            </div>
          ) : (
            <>
              <dl className="grid grid-cols-2 gap-y-3 text-sm">
                {summaryFields(schema.fields).map((field) => (
                  <div key={field.key} className="contents">
                    <dt className="text-[var(--bo-faint)]">{field.label}</dt>
                    <dd>{field.key === 'sourceFileName' && sharePointLinks(form).length ? <span className="inline-flex flex-wrap gap-x-3">{sharePointLinks(form).map((file) => <a key={file.url} className="text-[var(--bo-link)] underline" href={file.url} target="_blank" rel="noreferrer">{file.name}</a>)}</span> : schema.url === '/api/charge-lines' && field.key === 'poNumber' && form.poId ? <RecordLink to={`/purchase-orders/${form.poId}`} backLabel={`Back to ${String(form.name || 'charge line')}`}>{String(form.poNumber || 'Open order')}</RecordLink> : schema.url === '/api/payments-in' && field.key === 'invoiceNumber' && form.invoiceId ? <RecordLink to={`/invoices/${form.invoiceId}`} backLabel="Back to payments in">{String(form.invoiceNumber || 'Open invoice')}</RecordLink> : schema.url === '/api/payments-out' && field.key === 'billNumber' && form.billId ? <RecordLink to={`/bills/${form.billId}`} backLabel="Back to payments out">{String(form.billNumber || 'Open bill')}</RecordLink> : (schema.url === '/api/bills' || schema.url === '/api/payments-out') && field.key === 'poNumber' && form.poId ? <RecordLink to={`/supplier-purchase-orders/${form.poId}`} backLabel={`Back to ${String(form.number || form.billNumber || 'record')}`}>{String(form.poNumber || 'Open order')}</RecordLink> : displayField(form, field, lookups)}</dd>
                  </div>
                ))}
              </dl>
              {schema.url === '/api/engagements' && form[idKey] ? <RelatedRecords engagementId={String(form[idKey])} engagementName={String(form.name || '')} /> : null}
              <div className="mt-4 space-y-3">{editor()}</div>
            </>
          )}
        </ContextPanel>
      ) : null}
      {askInvoice && form && schema.url === '/api/purchase-orders' ? (
        <CreateInvoiceDialog
          purchaseOrderId={String(form[idKey] || '')}
          engagementId={String(form.engagementId || '')}
          currency={String(form.currency || 'GBP')}
          open={askInvoice}
          onClose={() => setAskInvoice(false)}
          onCreated={(invoiceId) => {
            setAskInvoice(false);
            navigate(`/invoices/${invoiceId}`);
          }}
        />
      ) : null}
      </div>
      {invoiceRow ? (
        <Dialog title={`Invoices on ${String(invoiceRow.poNumber || '')}`} onClose={() => setInvoiceRow(null)}>
          {Array.isArray(invoiceRow.invoices) && invoiceRow.invoices.length ? (
            <InvoiceLinks rows={invoiceRow.invoices as Row[]} poId={String(invoiceRow.id || '')} poNumber={String(invoiceRow.poNumber || '')} search={invoiceSearch} sort={invoiceSort} onSearch={(value) => { setInvoiceSearch(value); }} onSort={(key) => setInvoiceSort((current) => nextSort(current, key))} onOpen={() => setInvoiceRow(null)} />
          ) : <p className="text-sm text-gray-500">No invoices linked to this purchase order.</p>}
        </Dialog>
      ) : null}
      {askNewOffer ? <NewOfferDialog onClose={() => setAskNewOffer(false)} /> : null}
      {assist && assistKind(schema.url) ? (
        <RecordAssistDialog
          kind={assistKind(schema.url) || ''}
          mode={assist.mode}
          recordId={assist.recordId}
          current={assist.current}
          title={schema.title.replace(/s$/, '')}
          onClose={() => setAssist(null)}
          onManual={assist.mode === 'create' ? manualNew : undefined}
          onSaved={(saved) => {
            const kind = assistKind(schema.url) || '';
            const path = assistOpen(kind, { id: saved.id ? String(saved.id) : undefined, proposalId: saved.proposalId ? String(saved.proposalId) : undefined, quotationId: saved.quotationId ? String(saved.quotationId) : undefined });
            if (path) {
              navigate(path);
              return;
            }
            reload();
            if (saved.id) {
              setIsNew(false);
              setForm(editDraft(flattenRow(saved as Row), schema.fields));
            }
          }}
        />
      ) : null}
      {offerCopy ? <OfferDuplicateDialog proposalId={offerCopy.proposalId} quotationId={offerCopy.quotationId} onClose={() => setOfferCopy(null)} /> : null}
      {addPart ? (
        <AddOfferPartDialog
          kind={addPart.kind}
          customerId={addPart.offer.customerId}
          title={addPart.offer.title}
          proposalId={addPart.kind === 'quotation' ? addPart.offer.proposalId : undefined}
          quotationId={addPart.kind === 'proposal' ? addPart.offer.quotationId : undefined}
          onClose={() => setAddPart(null)}
        />
      ) : null}
      {offerDelete ? (
        <Dialog title="Delete sales offer" onClose={() => setOfferDelete(null)}>
          <p className="text-sm text-gray-600 dark:text-gray-300">This row has a proposal and a quotation. Choose what to delete.</p>
          <div className="flex flex-wrap gap-2">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => void removeOffer(offerDelete, 'proposal')}>Proposal</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => void removeOffer(offerDelete, 'quotation')}>Quotation</Button>
            <Button variant="destructive" onClick={() => void removeOffer(offerDelete, 'both')}>Both</Button>
          </div>
        </Dialog>
      ) : null}
      {historyOpen ? (
        <Dialog title="History" wide onClose={() => setHistoryOpen(false)}>
          {history.length ? history.map((entry) => (
            <div key={entry.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                <span>{new Date(entry.at).toLocaleString()} · {entry.actor}</span>
                <Button variant="outline" onClick={() => {
                  if (!historyTarget) return;
                  void apiPost(`/api/revisions/${historyTarget.type}/${historyTarget.id}/restore`, { revisionId: entry.id }).then(() => { setHistoryOpen(false); reload(); });
                }}>Restore</Button>
              </div>
              {(entry.changes || []).map((change) => (
                <div key={change.field} className="mt-2 text-sm">
                  <div className="font-medium">{change.field}</div>
                  <SideBySideDiff before={historyValue(change.field, change.from)} after={historyValue(change.field, change.to)} />
                </div>
              ))}
            </div>
          )) : <p className="text-sm text-gray-500">No earlier versions.</p>}
        </Dialog>
      ) : null}
      {pendingDelete ? (
        <Dialog
          title="Delete record"
          onClose={() => setPendingDelete(null)}
          footer={<><Button variant="outline" onClick={() => setPendingDelete(null)}>Cancel</Button><Button variant="destructive" onClick={() => void remove(pendingDelete)}>Delete</Button></>}
        >
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {pendingDelete.crunchInvoiceId || pendingDelete.crunchExpenseId
              ? 'This will also delete the Crunch record. If Crunch refuses, this row stays.'
              : 'Delete this record?'}
          </p>
        </Dialog>
      ) : null}
    </div>
  );

  function editor() {
    if (!form) return null;
    const offer = schema.url === '/api/commercial' ? offerOf(form) : null;
    const revenueRecord = schema.url === '/api/customers' || schema.url === '/api/reporting-customers';
    const tabs = revenueRecord
      ? [{ id: 'edit', label: 'Edit' }, { id: 'revenue', label: 'Revenue' }]
      : offer && offer.proposalId && offer.quotationId
        ? [{ id: 'proposal', label: 'Proposal' }, { id: 'quotation', label: 'Quotation' }]
        : [];
    const historyType = offer
      ? (paneTab === 'quotation' ? 'quotation' : 'proposal')
      : ({
        '/api/customers': 'customer',
        '/api/engagements': 'engagement',
        '/api/charge-lines': 'charge-line',
        '/api/vendors': 'vendor',
        '/api/expenses': 'expense',
        '/api/people': 'person',
        '/api/payments-in': 'payment-in',
        '/api/payments-out': 'payment-out',
        '/api/purchase-orders': 'purchase-order',
        '/api/bills': 'bill',
      } as Record<string, string>)[schema.url];
    const historyId = offer
      ? (paneTab === 'quotation' ? offer.quotationId : offer.proposalId)
      : String(form[idKey] || '');
    const books = schema.url === '/api/bills' ? sterlingBooksLine(form.currency, poundsToPence(form.gbpAmountPence), form.fxRate, form.fxRateOn) : '';
    return (
      <div className="space-y-3">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <div className="flex h-7 shrink-0 gap-1">
            {tabs.map((tab) => (
              <button key={tab.id} type="button" className={`h-7 rounded-md px-2 text-xs ${paneTab === tab.id ? 'bg-blue-600 text-white' : 'border border-gray-300 dark:border-gray-600'}`} onClick={() => dirty.requestLeave(() => setPaneTab(tab.id))}>{tab.label}</button>
            ))}
          </div>
          <ActionCluster>
            {historyType && historyId ? <Button variant="outline" onClick={() => showActivity()}>Activity</Button> : null}
            {schema.detailPath && !isNew ? <Button variant="outline" onClick={() => navigate(`${schema.detailPath}/${form[idKey]}`)}>Open full record</Button> : null}
            {canWrite && !isNew && duplicateLists.has(schema.url) ? <Button variant="outline" onClick={() => void duplicate(form)}>Duplicate</Button> : null}
            {schema.url === '/api/purchase-orders' && !isNew && can('invoices', 'draft') ? <Button onClick={() => setAskInvoice(true)}>Create invoice</Button> : null}
            {canWrite && !isNew && assistKind(schema.url) && assistKind(schema.url) !== 'sales-offer' ? <Button variant="outline" disabled={!assistEditable(assistKind(schema.url) || '', form.status)} title={assistEditable(assistKind(schema.url) || '', form.status) ? 'Edit with AI' : 'This status cannot be edited'} onClick={() => setAssist({ mode: 'edit', recordId: String(form[idKey] || ''), current: toPayload(form, schema.fields) })}>Edit with AI</Button> : null}
            {canWrite && String(form.status || '') !== 'issued' ? <Button variant="outline" onClick={() => void save()}>Save</Button> : null}
            {!isNew && (canWrite || canVoid) ? <Button variant="destructive" onClick={() => {
              if (offer) setOfferDelete(offer);
              else setPendingDelete(form);
            }}>Delete</Button> : null}
          </ActionCluster>
        </div>
        {revenueRecord && paneTab === 'revenue' ? <CustomerRevenue customerId={String(form[idKey])} /> : null}
        {offer && paneTab === 'proposal' && offer.proposalId ? <ProposalPage recordId={offer.proposalId} /> : null}
        {offer && paneTab === 'quotation' && offer.quotationId ? <QuotationPage recordId={offer.quotationId} /> : null}
        {offer && paneTab === 'proposal' && !offer.proposalId && canWrite ? <button type="button" className="inline-flex h-7 items-center rounded-md border border-gray-300 px-2 text-xs" onClick={() => setAddPart({ kind: 'proposal', offer })}>Add proposal</button> : null}
        {offer && paneTab === 'quotation' && !offer.quotationId && canWrite ? <button type="button" className="inline-flex h-7 items-center rounded-md border border-gray-300 px-2 text-xs" onClick={() => setAddPart({ kind: 'quotation', offer })}>Add quotation</button> : null}
        {books ? <p className="text-sm text-gray-500">{books}</p> : null}
        {!offer && !(revenueRecord && paneTab === 'revenue') ? schema.fields.filter((field) => !field.hideInForm).map((field) => (
          <Field key={field.key} label={field.required ? `${field.label} *` : field.label}>
            {field.type === 'select' ? (
              <Select searchable={Boolean(field.lookup)} choices={field.lookup ? [{ value: '', label: 'Select' }, ...selectOptions(field)] : undefined} onOpen={field.lookup ? () => reloadLookup(field.lookup || '') : undefined} disabled={String(form.status || '') === 'issued'} value={String(form[field.key] ?? '')} onChange={(event) => setField(field.key, event.target.value)}>
                {field.lookup ? null : <><option value="">Select</option>{selectOptions(field).map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</>}
              </Select>
            ) : field.type === 'checkbox' ? (
              <input type="checkbox" className="mt-1 h-4 w-4" disabled={String(form.status || '') === 'issued'} checked={Boolean(form[field.key])} onChange={(event) => setField(field.key, event.target.checked)} />
            ) : field.type === 'textarea' ? (
              <TextArea disabled={String(form.status || '') === 'issued'} value={String(form[field.key] ?? '')} onChange={(event) => setField(field.key, event.target.value)} />
            ) : field.type === 'tags' ? (
              <ChipList values={Array.isArray(form[field.key]) ? form[field.key] as string[] : []} onChange={(next) => setField(field.key, next)} />
            ) : (
              <Input
                type={field.type === 'number' || field.type === 'money' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                step={field.type === 'money' || field.type === 'number' ? '0.01' : undefined}
                disabled={String(form.status || '') === 'issued'}
                value={String(form[field.key] ?? '')}
                onChange={(event) => setField(field.key, event.target.value)}
              />
            )}
          </Field>
        )) : null}
        {schema.url === '/api/charge-lines' && form.poId ? <RecordLink to={`/purchase-orders/${form.poId}`} backLabel={`Back to ${String(form.name || 'charge line')}`}>Open order</RecordLink> : null}
        {schema.url === '/api/payments-in' && form.invoiceId ? <RecordLink to={`/invoices/${form.invoiceId}`} backLabel="Back to payments in">Open invoice</RecordLink> : null}
        {schema.url === '/api/payments-in' && Array.isArray(form.orders) ? (form.orders as { id?: string; poNumber?: string }[]).filter((order) => order.id).map((order) => (
          <RecordLink key={order.id} to={`/purchase-orders/${order.id}`} backLabel="Back to payments in">Open order {order.poNumber || ''}</RecordLink>
        )) : null}
        {schema.url === '/api/payments-out' && form.billId ? <RecordLink to={`/bills/${form.billId}`} backLabel="Back to payments out">Open bill</RecordLink> : null}
        {schema.url === '/api/payments-out' && form.poId ? <RecordLink to={`/supplier-purchase-orders/${form.poId}`} backLabel="Back to payments out">Open purchase order</RecordLink> : null}
        {schema.url === '/api/bills' && form.poId ? <RecordLink to={`/supplier-purchase-orders/${form.poId}`} backLabel={`Back to ${String(form.number || 'bill')}`}>Open purchase order</RecordLink> : null}
      </div>
    );
  }
}

function formatUpdated(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function historyTypeFor(url: string): string {
  const types: Record<string, string> = {
    '/api/customers': 'customer',
    '/api/engagements': 'engagement',
    '/api/charge-lines': 'charge-line',
    '/api/vendors': 'vendor',
    '/api/expenses': 'expense',
    '/api/people': 'person',
    '/api/payments-in': 'payment-in',
    '/api/payments-out': 'payment-out',
    '/api/bills': 'bill',
    '/api/purchase-orders': 'purchase-order',
  };
  return types[url] || '';
}

function RelatedRecords({ engagementId, engagementName }: { engagementId: string; engagementName: string }) {
  const offers = useQuery({ queryKey: ['records', '/api/commercial'], queryFn: () => apiGet<Row[]>('/api/commercial') });
  const orders = useQuery({ queryKey: ['records', '/api/purchase-orders'], queryFn: () => apiGet<Row[]>('/api/purchase-orders') });
  const lines = useQuery({ queryKey: ['records', '/api/charge-lines'], queryFn: () => apiGet<Row[]>('/api/charge-lines') });
  const commercial = offers.data || [];
  const offerIds = new Set<string>();
  for (const row of commercial) {
    if (String(row.engagementId || '') !== engagementId) continue;
    offerIds.add(row.kind === 'quotation' && row.proposalId ? String(row.proposalId) : String(row.id));
  }
  const offerCount = offerIds.size;
  const orderCount = (orders.data || []).filter((row) => String(row.engagementId || '') === engagementId).length;
  const lineCount = (lines.data || []).filter((row) => String(row.engagementId || '') === engagementId).length;
  const scope = `engagementId=${encodeURIComponent(engagementId)}&engagementName=${encodeURIComponent(engagementName)}`;
  const rows = [
    { label: 'Sales offers', count: offerCount, to: `/quotations?${scope}`, icon: Send },
    { label: 'Customer orders', count: orderCount, to: `/purchase-orders?${scope}`, icon: FileText },
    { label: 'Charge lines', count: lineCount, to: `/charge-lines?${scope}`, icon: List },
  ];
  return (
    <div className="mt-4">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--bo-faint)]">Related records</div>
      {rows.map((row) => (
        <Link key={row.label} className="flex items-center justify-between border-t border-[var(--bo-border)] py-2 text-sm" to={row.to}>
          <span className="inline-flex items-center gap-2"><row.icon className="h-4 w-4 text-[var(--bo-muted)]" />{row.label}</span><span>{row.count}</span>
        </Link>
      ))}
    </div>
  );
}

function InvoiceLinks({ rows, poId, poNumber, search, sort, onSearch, onSort, onOpen }: {
  rows: Row[];
  poId: string;
  poNumber: string;
  search: string;
  sort: ColumnSort | null;
  onSearch: (value: string) => void;
  onSort: (key: string) => void;
  onOpen: () => void;
}) {
  const columnSearch = useColumnSearch();
  const columns = [
    { key: 'number', label: 'Invoice', text: (row: Row) => `${String(row.number || '')} ${String(row.internalNumber || '')}` },
    { key: 'issuedOn', label: 'Issued', text: (row: Row) => String(row.issuedOn || ''), sort: (row: Row) => String(row.issuedOn || '') },
    { key: 'status', label: 'Status', text: (row: Row) => enumLabel(String(row.status || '')) },
    { key: 'netPence', label: 'Net', text: (row: Row) => displayField(row, { key: 'netPence', label: 'Net', type: 'money' }), sort: (row: Row) => Number(row.netPence) },
    { key: 'vatPence', label: 'VAT', text: (row: Row) => displayField(row, { key: 'vatPence', label: 'VAT', type: 'money' }), sort: (row: Row) => Number(row.vatPence) },
  ];
  const shown = applyTableQuery(rows, search, columns, sort).filter((row) => columnSearch.matches(Object.fromEntries(columns.map((column) => [column.key, String(column.text(row) ?? '')]))));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Input aria-label="Search invoices" placeholder="Search invoices" value={search} onChange={(event) => onSearch(event.target.value)} />
        <ExcelButton filename={`${poNumber || 'order'} invoices`} rows={shown.map((inv) => [
          String(inv.number || ''),
          String(inv.issuedOn || ''),
          enumLabel(String(inv.status || '')),
          excelCell(inv, { key: 'netPence', label: 'Net', type: 'money' }),
          excelCell(inv, { key: 'vatPence', label: 'VAT', type: 'money' }),
        ])} headers={['Invoice', 'Issued', 'Status', 'Net', 'VAT']} />
      </div>
      <table className="bo-cards w-full text-sm">
        <thead>
          <tr>
            {columns.map((column) => (
              <SortHeader key={column.key} className="py-2 pr-3 font-medium" label={column.label} active={sort?.key === column.key} dir={sort?.dir || 'asc'} onSort={() => onSort(column.key)} />
            ))}
            <th className="text-right"><ColumnFilterToggle open={columnSearch.open} onClick={columnSearch.toggle} /></th>
          </tr>
          {columnSearch.open ? <ColumnFilterInputs columns={columns.map((column) => ({ key: column.key, label: column.label }))} draft={columnSearch.draft} onChange={columnSearch.set} trailing /> : null}
        </thead>
        <tbody>
          {shown.map((inv) => (
            <tr key={String(inv.id)}>
              <td data-label="Invoice">
                <div>{String(inv.number || '')}</div>
                {inv.internalNumber && inv.internalNumber !== inv.number ? <div className="text-xs text-[var(--bo-faint)]">{String(inv.internalNumber)}</div> : null}
              </td>
              <td data-label="Issued">{String(inv.issuedOn || '')}</td>
              <td data-label="Status">{enumLabel(String(inv.status || ''))}</td>
              <td data-label="Net">{displayField(inv, { key: 'netPence', label: 'Net', type: 'money' })}</td>
              <td data-label="VAT">{displayField(inv, { key: 'vatPence', label: 'VAT', type: 'money' })}</td>
              <td className="text-right" data-label=""><RecordLink className="text-xs underline" to={`/invoices/${inv.id}`} backLabel={`Back to ${poNumber || 'order'}`} backTo={poId ? `/purchase-orders/${poId}` : undefined} onClick={onOpen}>Open</RecordLink></td>
            </tr>
          ))}
        </tbody>
      </table>
      {!shown.length ? <p className="text-sm text-gray-500">No invoices match.</p> : null}
    </div>
  );
}

function summaryFields(fields: FieldDef[]): FieldDef[] {
  const hidden = fields.filter((field) => field.hideInForm);
  const shown = hidden.slice(0, 6);
  const file = hidden.find((field) => field.key === 'sourceFileName');
  return file && !shown.some((field) => field.key === file.key) ? [...shown, file] : shown;
}

function historyValue(field: string, value: unknown): string {
  if (value == null || value === '') return '';
  if (field.endsWith('Pence') && typeof value === 'number') return formatMoney(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function CustomerRevenue({ customerId }: { customerId: string }) {
  const query = useQuery({
    queryKey: ['revenue', customerId],
    queryFn: () => apiGet<{ spread: Record<string, number>; committedPence: number; quotedPence: number; reportingCurrency?: string; exchangeRateBasis?: string; billingEntities?: { id: string; name: string; original: Record<string, number>; gbpNetPence: number }[] }>(`/api/finance/revenue?customerId=${encodeURIComponent(customerId)}`),
  });
  if (!query.data) return <p className="text-sm text-gray-500">{query.isLoading ? 'Loading' : 'No revenue.'}</p>;
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3"><div>Committed {formatMoney(query.data.committedPence)}</div><div>Quoted {formatMoney(query.data.quotedPence)}</div></div>
      {query.data.billingEntities?.length ? (
        <div className="overflow-x-auto">
          <table className="min-w-full"><thead><tr className="border-b text-left text-gray-500"><th className="py-2">Billing entity</th><th className="py-2">Original currency</th><th className="py-2 text-right">GBP net</th></tr></thead>
            <tbody>{query.data.billingEntities.map((row) => <tr key={row.id} className="border-b"><td className="py-2">{row.name}</td><td className="py-2">{Object.entries(row.original).map(([currency, amount]) => formatMoney(amount, currency)).join(' · ')}</td><td className="py-2 text-right">{formatMoney(row.gbpNetPence, 'GBP')}</td></tr>)}</tbody>
          </table>
          <p className="mt-2 text-xs text-gray-500">Reporting currency {query.data.reportingCurrency || 'GBP'} · {query.data.exchangeRateBasis || 'Bank of England rate on invoice date'}</p>
        </div>
      ) : null}
    </div>
  );
}
